/**
 * Dimensions overlay — ports the production viewer's measurement look (SVG lines
 * + arrowheads + dark label pills, `inbox/viewer-features-extract.md` §2) to the
 * three.js configurator.
 *
 * KEY DIFFERENCE from the prod viewer: prod reads AUTHORED `from`/`to` strings
 * from config because its model is static. The configurator's shape changes
 * (2-seater vs L-shape have different sizes), so authored lines would be wrong on
 * every switch. Here the three measurement lines are derived from the LIVE
 * assembly bounding box (the same box that feeds getDimensions, already verified
 * accurate: 2-seater 242×101×77, L-shape 388×231×82). Same visual, correct for a
 * modular product.
 *
 * SEPARATED PLANES (the fix Alex already solved on the prod viewers): the three
 * lines must NOT share a corner or they pile up into a mess. Each cote sits on a
 * different edge so they never converge. Layout per Alex 2026-05-30:
 *   width  (X) — FRONT-bottom edge   (y=min, z=max)
 *   height (Y) — BACK-LEFT vertical  (x=min, z=min)   ← unchanged
 *   depth  (Z) — TOP-right edge      (x=max, y=max)   ← "on top"
 * Labels are pushed OUTWARD from the box centre (in 3D) so they clear the line.
 * Each redraws on camera change + on every rebuild. Toggled from the viewer menu.
 */

import * as THREE from "three";
import type { Engine } from "@/engine/Engine";

const SVG_NS = "http://www.w3.org/2000/svg";

interface DimLine {
  /** World endpoints of the measurement line. */
  a: THREE.Vector3;
  b: THREE.Vector3;
  /** World-space label anchor (line midpoint pushed outward from box centre, so
   *  the pill clears the line + sits on the outside of the product). */
  labelPos: THREE.Vector3;
  line: SVGLineElement;
  label: HTMLDivElement;
  /** Current visibility — so redraw only writes `display` when it actually flips
   *  (avoids a per-frame style invalidation on every line + label during orbit). */
  shown: boolean;
}

export interface DimensionsOverlayApi {
  /** Show/hide the measurement overlay. */
  setActive(on: boolean): void;
  isActive(): boolean;
  /** Recompute geometry from the current assembly box (call after a rebuild). */
  refresh(): void;
  /** Tear down DOM + listeners. */
  destroy(): void;
}

/** cm string from a world-space (metre) span. */
function cm(span: number): string {
  return `${Math.round(span * 100)} cm`;
}

export function createDimensionsOverlay(
  engine: Engine,
  viewer: HTMLElement,
  opts: { onDismiss?: () => void } = {},
): DimensionsOverlayApi {
  let active = false;

  // SVG overlay (lines) — z-index 4: above the canvas, below the glass menu (6).
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.classList.add("dim-svg");
  svg.style.display = "none";

  const defs = document.createElementNS(SVG_NS, "defs");
  const marker = document.createElementNS(SVG_NS, "marker");
  marker.setAttribute("id", "dim-arrowhead");
  // Arrowhead sized ~33% smaller than the original 9×10 (Alex: takes less screen
  // space). All dims scaled together so the tip still sits on the line endpoint.
  marker.setAttribute("markerWidth", "6");
  marker.setAttribute("markerHeight", "6.7");
  marker.setAttribute("refX", "3");
  marker.setAttribute("refY", "3.35");
  // auto-start-reverse flips the start marker so both arrowheads point OUTWARD
  // (←———→). Plain "auto" makes both point the same way along a→b. (prod _dim-test.html)
  marker.setAttribute("orient", "auto-start-reverse");
  const arrow = document.createElementNS(SVG_NS, "path");
  arrow.setAttribute("d", "M0,0.7 L6,3.35 L0,6 Z");
  arrow.setAttribute("fill", "rgba(0,0,0,0.7)");
  marker.appendChild(arrow);
  defs.appendChild(marker);
  svg.appendChild(defs);

  viewer.appendChild(svg);

  // Three measurement lines + label pills. Endpoints are filled by build().
  const dims: DimLine[] = [];
  const makeLine = (): DimLine => {
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("class", "dim-svg-line");
    line.setAttribute("marker-start", "url(#dim-arrowhead)");
    line.setAttribute("marker-end", "url(#dim-arrowhead)");
    svg.appendChild(line);
    const label = document.createElement("div");
    label.className = "dim-label";
    viewer.appendChild(label);
    return { a: new THREE.Vector3(), b: new THREE.Vector3(), labelPos: new THREE.Vector3(), line, label, shown: true };
  };
  const wLine = makeLine();
  const hLine = makeLine();
  const dLine = makeLine();

  /** Recompute world endpoints from the current assembly bounding box, placing
   *  each cote on a SEPARATE face so they never converge (prod-viewer layout). */
  function build(): void {
    const box = engine.getAssemblyBox();
    if (!box) return;
    const { min, max } = box;
    const span = Math.max(max.x - min.x, max.y - min.y, max.z - min.z) || 1;
    const off = span * 0.1; // outward label nudge, scale-relative, clears the line

    // width (X): FRONT-bottom edge → label pushed forward + down.
    wLine.a.set(min.x, min.y, max.z);
    wLine.b.set(max.x, min.y, max.z);
    wLine.labelPos.set((min.x + max.x) / 2, min.y - off, max.z + off);

    // height (Y): BACK-LEFT vertical edge → label pushed left + back. (unchanged)
    hLine.a.set(min.x, min.y, min.z);
    hLine.b.set(min.x, max.y, min.z);
    hLine.labelPos.set(min.x - off, (min.y + max.y) / 2, min.z - off);

    // depth (Z): TOP-right edge → label pushed right + up ("on top").
    dLine.a.set(max.x, max.y, min.z);
    dLine.b.set(max.x, max.y, max.z);
    dLine.labelPos.set(max.x + off, max.y + off, (min.z + max.z) / 2);

    wLine.label.textContent = cm(max.x - min.x);
    hLine.label.textContent = cm(max.y - min.y);
    dLine.label.textContent = cm(max.z - min.z);
  }

  /** Project endpoints to screen + redraw lines/labels for the current camera. */
  function redraw(): void {
    if (!active) return;
    for (const d of dims) {
      // All projection READS first (no DOM writes interleaved → getBoundingClientRect
      // stays cached, no forced reflow mid-loop).
      const pa = engine.projectToScreen(d.a);
      const pb = engine.projectToScreen(d.b);
      const pl = engine.projectToScreen(d.labelPos);
      const show = !(pa.behind || pb.behind);
      // Only touch `display` when visibility actually flips — writing it every frame
      // invalidates style on each line + label and is a big chunk of the orbit cost.
      if (show !== d.shown) {
        d.shown = show;
        d.line.style.display = show ? "" : "none";
        d.label.style.display = show ? "" : "none";
      }
      if (!show) continue;
      d.line.setAttribute("x1", String(pa.x));
      d.line.setAttribute("y1", String(pa.y));
      d.line.setAttribute("x2", String(pb.x));
      d.line.setAttribute("y2", String(pb.y));
      // Label sits at its own 3D anchor (midpoint pushed outward from the box),
      // projected to screen. Position via a GPU transform (compositor-only — no
      // per-frame layout/paint that `left/top` would force), with the pre-promoted
      // `will-change` layer so the FIRST move of each gesture doesn't hitch on layer
      // promotion. The trailing translate(-50%,-50%) keeps the pill centred.
      d.label.style.transform = `translate3d(${pl.x}px, ${pl.y}px, 0) translate(-50%, -50%)`;
    }
  }

  dims.push(wLine, hLine, dLine);

  function apply(on: boolean): void {
    active = on;
    svg.style.display = on ? "" : "none";
    for (const d of dims) {
      d.label.style.display = on ? "" : "none";
    }
    if (on) { build(); redraw(); }
  }

  // Redraw on camera move (orbit/zoom/reframe) and on every rebuild.
  const offCam = engine.onCameraChange(() => redraw());
  engine.onAssemblyShown(() => {
    if (active) {
      build();
      redraw();
      // A shape/size switch re-ran frameBox (tight home); re-dolly out so the new
      // (often bigger) box's labels still fit while Dimensions stays open.
      engine.zoomOutToFit();
    }
  });

  // A real TAP on the viewer (no drag, no pinch) clears Dimensions — like tapping to
  // dismiss. A drag (rotate) or a two-finger gesture (pinch-zoom) keeps it. Mirrors
  // the PartsOverlay tap-to-dismiss. onDismiss lets the caller restore the camera +
  // the menu item state.
  let downX = 0;
  let downY = 0;
  let pointerCount = 0;
  let multi = false;
  const onDown = (e: PointerEvent): void => {
    pointerCount++;
    if (pointerCount > 1) { multi = true; return; }
    multi = false;
    downX = e.clientX;
    downY = e.clientY;
  };
  const onUp = (e: PointerEvent): void => {
    const wasMulti = multi;
    pointerCount = Math.max(0, pointerCount - 1);
    if (pointerCount === 0) multi = false;
    if (!active) return;
    if (wasMulti) return;                                              // pinch-zoom → keep
    if (Math.hypot(e.clientX - downX, e.clientY - downY) >= 8) return; // drag/rotate → keep
    const t = e.target as HTMLElement | null;
    if (t && t.closest("button")) return;                             // tapped a control → ignore
    apply(false);
    opts.onDismiss?.();
  };
  viewer.addEventListener("pointerdown", onDown);
  viewer.addEventListener("pointerup", onUp);

  return {
    setActive(on: boolean): void {
      apply(on);
    },
    isActive: () => active,
    refresh(): void {
      if (active) { build(); redraw(); }
    },
    destroy(): void {
      offCam();
      viewer.removeEventListener("pointerdown", onDown);
      viewer.removeEventListener("pointerup", onUp);
      svg.remove();
      for (const d of dims) d.label.remove();
      dims.length = 0;
    },
  };
}
